import React, { lazy } from 'react'

import IconButton from '@material-ui/core/IconButton'
import Paper from '@material-ui/core/Paper'
import Slide from '@material-ui/core/Slide'
import { withStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import CloseIcon from '@material-ui/icons/Close'
import cloneDeep from 'lodash/cloneDeep'
import filter from 'lodash/filter'
import get from 'lodash/get'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import { debounceSearchRender } from 'mui-datatables'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import GetContentType, { CONTENT_TYPE_URL } from 'drawer/tabs/info/panels/ContentType'
import { setLayerFilter, setLayerPanelsFeatureOpen } from 'drawer/tabs/layers/actions'
import { closeTable, setTableStatus } from 'drawer/tabs/layers/table/actions'
import PoziMap from 'map/map'
import PoziZoom from 'map/zoom'
import { getMap } from 'utils/MapUtils'
import { linkifyText } from 'utils/url'

const MUIDataTable = lazy(() => import('mui-datatables'))

export const TABLE_STATUS_CLOSED = 'closed'
export const TABLE_STATUS_CLOSING = 'closing'
export const TABLE_STATUS_OPENED = 'opened'
export const TABLE_STATUS_OPENING = 'opening'

const styles = theme => ({
  root: {
    position: 'fixed',
    width: '100%',
    right: 0,
    bottom: 0,
    zIndex: 4,
    backgroundColor: 'white'
  },
  paper: {},
  tableRoot: {
    height: 40
  },
  tableRow: {},
  tableCell: {},
  closeButton: {
    float: 'right',
    zIndex: 1 // needs to be higher than the table on small devices
  },
  titleT: {
    fontSize: 14,
    height: 34,
    padding: 10,
    paddingLeft: 22,
    float: 'left',
    color: '#212121'
  },
  closeT: {
    float: 'right'
  },
  // Hacks to change the style of the datatable
  dataTable: {
    '& td,th': {
      verticalAlign: 'top',
      padding: '6px 15px',
      whiteSpace: 'nowrap'
    },
    // Reduce the height of the top bar of the table
    '& .MuiToolbar-root': {
      minHeight: 'initial',
      paddingLeft: 15
    },
    // This will reduce the height of the footer. It's the padding of the pagination
    // buttons that give it that height
    '& #pagination-back,#pagination-next': {
      padding: '0px 10px'
    }
  }
})

// Allow for non-blocking renders of components
// https://stackoverflow.com/a/58618716
// export const DeferredRender = ({ children, idleTimeout }) => {
//   const [render, setRender] = React.useState(false)

//   React.useEffect(() => {
//     if (render) setRender(false)
//     const id = requestIdleCallback(() => setRender(true), { timeout: idleTimeout })

//     return () => cancelIdleCallback(id)
//   }, [idleTimeout])

//   if (!render) return null

//   return children
// }

class LayerTable_ extends React.Component {
  constructor (props) {
    super(props)

    const { geojson, titleid } = this.props

    this.pure = {}
    this.pure.search = this.search.bind(this)
    this.pure.onFilterChange = this.onFilterChange.bind(this)
    this.pure.rowClicked = this.rowClicked.bind(this)
    this.pure.rowSelected = this.rowSelected.bind(this)
    this.pure.parseGeoJSON = this.parseGeoJSON.bind(this)

    this.searchField = null

    this.options = {
      // Only download visible table
      downloadOptions: {
        filterOptions: {
          useDisplayedColumnsOnly: true,
          useDisplayedRowsOnly: true
        }
      },

      customSearchRender: debounceSearchRender(300),
      filterType: 'multiselect',
      print: false,
      fixedHeader: true,
      searchProps: {
        // Make search field available so we can focus on it
        inputRef: (el) => { this.searchField = el }
      },
      selectableRows: 'single',
      selectableRowsHideCheckboxes: true,
      selectableRowsOnClick: true,
      selectToolbarPlacement: 'none', // Never show the select toolbar
      pagination: true,
      rowsPerPage: 100, // More than 100 slows down the table significantly
      rowsPerPageOptions: [10, 20, 50, 100, 200, 500, 1000], // And empty array hides the rows per page selection list
      draggableColumns: {
        enabled: true
      },
      responsive: 'standard', // 'standard', 'simple', 'vertical'
      tableBodyHeight: '30vh',
      onRowClick: this.pure.rowClicked, // Will not fire when row selection is enabled!
      onRowSelectionChange: this.pure.rowSelected,
      onFilterChange: this.pure.onFilterChange,
      onSearchChange: this.pure.search,
      // onSearchOpen: this.pure.searchChange,
      // onSearchClose: this.pure.searchChange,
      textLabels: {
        body: {
          noMatch: 'None'
        }
      }
    }

    this.state = {
      featurePropertiesArr: [],
      dataIdColumnsIndex: '',
      geojson: {},
      columns: [],
      data: []
    }

    if (geojson) {
      // console.debug('LayerTable - geojson', this.dataPath, geojson)

      this.state = this.pure.parseGeoJSON(geojson)
    }
  }

  componentDidUpdate (prevProps) {
    const { geojson } = this.props

    if (geojson === prevProps.geojson) {
      return
    }

    // console.debug('LayerTable - componentDidUpdate, geojson)

    // Use parseGeoJSON to set the new application state
    const newState = {
      ...this.state,
      ...this.parseGeoJSON()
    }

    // console.debug('LayerTable - componentDidUpdate - newState', newState)

    this.setState(newState)
  }

  // Customise the cell rendering
  customBodyRender = (value) => {
    // Render 'null' and 'undefined' as empty string
    const strValue = String(value)
    if (strValue === 'null' || strValue === 'undefined') {
      return ''
    }

    // Render URLs as clickable links
    const isUrl = GetContentType(value) === CONTENT_TYPE_URL
    if (isUrl) {
      value = linkifyText(value)
    }

    return value
  }

  rowSelected (...args) {
    // 3rd argument is an array of selected row ID's. Since we only allow selecting
    // one row at a time, we're interested in the first item of that array.
    const rowId = args[2][0]
    const rowData = this.state.data[rowId]

    // Call the function that deals with clicked rows (not the same as selected!)
    this.rowClicked(rowData)
  }

  rowClicked (rowData) {
    const { titleid } = this.props

    PoziMap().unselectAllFeatures()

    if (rowData instanceof Array && rowData.length > 0) {
      const idVal = rowData[this.state.dataIdColumnsIndex]
      const olFeature = PoziUtils.selectVectorFeatureFromIDTextAndLayerConfig(idVal, titleid)
      if (!olFeature) {
        console.log('LayerTable - no feature found!', titleid, idVal, this.state.dataIdColumnsIndex, rowData)
        return
      }

      // Extract geometry type
      const olGeometry = olFeature.getGeometry()
      const geomTypeOrig = olGeometry?.getType() || ''
      const geomTypeSimple = PoziUtils.getGeomTypeSimple(geomTypeOrig)

      if (geomTypeSimple === 'nogeometry') {
        console.log('LayerTable - feature has no geometry, so not zooming to it.')
      }

      // Check if the feature's layer is visible at the current zoom level.  If zoomed too far in or out, pan to
      // the feature at the first available zoom level.
      const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)
      const { maxResolution = Infinity, minResolution = 0 } = olLayer.getProperties()
      const currentView = getMap().getView()
      const mapResolution = currentView.getResolution() || 0
      let zoomTo = currentView.getZoom()

      if (mapResolution < minResolution) {
        zoomTo = Math.floor(currentView.getZoomForResolution(minResolution))
      } else if (mapResolution > maxResolution) {
        zoomTo = Math.ceil(currentView.getZoomForResolution(maxResolution))
      }
      PoziZoom().zoomPanTo(olFeature.getGeometry(), zoomTo)
    } else {
      console.log('LayerTable - no row data, so not selecting a feature. Maybe user unselected a row?')
    }
  }

  search (searchStr) {
    const { titleid, layerPropsFilter, setLayerFilter, dataIdField } = this.props
    if (!searchStr) {
      searchStr = ''
    } else {
      searchStr = searchStr.toString().toLowerCase()
    }

    // console.debug('LayerTable - search changed for layer', searchStr, titleid)

    setLayerFilter(titleid, layerPropsFilter)

    const featArr = filter(this.state.data, function (oArr) {
      if (!searchStr || searchStr === '') {
        return true
      }

      // Convert each item to string and take care of null or undefined values
      const oArrLower = oArr.map(v => isNil(v) ? '' : String(v).toLowerCase())

      // Check if any of the items contains the search string
      const isMatch = oArrLower.some((str) => str.includes(searchStr))

      return isMatch
    })

    const idArray = featArr.map(feature => feature[this.state.dataIdColumnsIndex])

    PoziMap().showFeaturesInLayerWithValArray(titleid, dataIdField, idArray)
  }

  onClose = (evt) => {
    // console.debug('LayerTable - onClose')

    const { dispatchCloseTable, handleCloseTable, setLayerPanelsFeatureOpen, titleid } = this.props

    // Roundabout way to close the table layer menu item for this layer
    setLayerPanelsFeatureOpen(titleid, '')

    dispatchCloseTable()

    if (typeof handleCloseTable === 'function') {
      handleCloseTable(evt)
    }
  }

  onFilterChange (...args) {
    const { titleid, layerPropsFilter, setLayerFilter, dataIdField } = this.props
    const data = args[4] || []

    // console.debug('LayerTable - onFilterChange', titleid, args, this.state)

    let idArray

    // Get feature ID from data
    if (data.length > 0) {
      idArray = data.map(row => row.data[this.state.dataIdColumnsIndex])
    } else {
      // Filter reset gives us no data. We'll show all data using the stored state
      idArray = this.state.data.map(row => row[this.state.dataIdColumnsIndex])
    }

    setLayerFilter(titleid, layerPropsFilter)

    // console.debug('onFilterChange - idArray', titleid, dataIdField, idArray, this.state.dataIdColumnsIndex, layerPropsFilter, data)

    PoziMap().showFeaturesInLayerWithValArray(titleid, dataIdField, idArray)
  }

  parseGeoJSON () {
    const { dataIdField, exclude: globalExclude, geojson, layerAttributes, layerConf } = this.props

    const exclude = [...globalExclude, '$id']

    let initialState = {
      geojson
    }

    const featureProperties = cloneDeep(get(geojson, 'features[0].properties', {}))
    if (featureProperties) {
      const featurePropertiesArr = Object.keys(featureProperties)
      if (featurePropertiesArr.length > 0) {
        // If you exclude ID from the table then it cannot do a feature lookup.
        /* const exclude = get(layer, 'config.spatial.exclude', null)
          if (exclude) {
            remove(featurePropertiesArr, function (value) {
              return includes(exclude, value)
            })
          } */

        // featurePropertiesArr = featurePropertiesArr.sort()
        const columns = []

        initialState.featurePropertiesArr = featurePropertiesArr

        // Find the dataIdColumnsIndex
        for (const [index, item] of featurePropertiesArr.entries()) {
          let display = true

          // Don't show item if in exclude list
          if (includes(exclude, item)) {
            display = false
          }

          // Use field alias as name instead of value if provided in attributes
          // NOTE: this changes the data itself and therefore, affects the download
          //       too!
          const name = layerAttributes?.[item]?.alias || item

          columns.push({
            name,
            options: {
              customBodyRender: this.customBodyRender,
              display,
              filter: true,
              sort: true
            }
          })
          if (item === dataIdField) {
            initialState.dataIdColumnsIndex = index
          }
        }

        initialState.columns = columns

        const data = []
        for (const feature of geojson.features) {
          if (!feature.properties) {
            console.debug('LayerTable - parseGeoJSON - feature without properties. Ignoring', feature)
            continue
          }

          const dataRow = featurePropertiesArr.map((colName) => feature.properties?.[colName])

          data.push(dataRow)
        }
        initialState.data = data
      } else {
        initialState = {
          featurePropertiesArr: [],
          dataIdColumnsIndex: '',
          geojson: {},
          columns: [],
          data: []
        }
      }
    }

    return initialState
  }

  render () {
    const { classes, title, titleid, layerParent, layerSelected, layerVisible, tableOpen, setTableStatus } = this.props

    // console.debug('LayerTable - render', titleid, layerSelected, layerVisible, layerParent, tableOpen)

    const licensed = Model.get('config.layout.drawer.tabs.layers.table.enabled', false)
    if (!licensed) {
      // console.debug('LayerTable.render - not licensed')

      return null
    }

    const slideTimeout = {
      enter: 300,
      exit: 100
    }

    // The layer has to either be selected or be a special layer (with a parent)
    const showTable = !!titleid && tableOpen && (layerSelected || !!layerParent)

    if (!showTable) {
      // console.debug('LayerTable - render - not showing', titleid, tableOpen, layerVisible, `parent: '${layerParent}'`)
    }

    // console.debug('LayerTable - render - searchField', this.searchField)

    return (
      <Slide
        direction="up"
        in={showTable}
        appear
        // mountOnEnter
        // unmountOnExit
        timeout={slideTimeout}
        addEndListener={(node, done) => {
          console.debug('LayerTable - rendered...')
        }}
        onEntering= {() => setTableStatus(TABLE_STATUS_OPENING)}
        onEntered= {() => setTableStatus(TABLE_STATUS_OPENED) }
        onExiting= {() => setTableStatus(TABLE_STATUS_CLOSING)}
        onExited= {() => setTableStatus(TABLE_STATUS_CLOSED)}
      >
        <div className={classes.root}>
          <Paper className={classes.paper} elevation={1}>
            <Tooltip title="Close">
              <IconButton className={classes.closeButton} onClick={this.onClose} color="primary" aria-label="close" autoFocus>
                <CloseIcon />
              </IconButton>
            </Tooltip>
              <MUIDataTable
                className={classes.dataTable}
                columns={this.state.columns}
                data={this.state.data}
                options={this.options}
                title={title}
                />
          </Paper>
        </div>
      </Slide>
    )
  }
}

LayerTable_.propTypes = {
  classes: PropTypes.object.isRequired,
  geojson: PropTypes.object,
  layerAttributes: PropTypes.object,
  layerConf: PropTypes.object,
  layerPropsFilter: PropTypes.object,
  titleid: PropTypes.string.isRequired,
  handleCloseTable: PropTypes.func,
  setLayerFilter: PropTypes.func.isRequired,
  setLayerPanelsFeatureOpen: PropTypes.func.isRequired,
  dispatchCloseTable: PropTypes.func.isRequired,
  setTableStatus: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  layerParent: PropTypes.string.isRequired,
  layerType: PropTypes.string.isRequired,
  layerSelected: PropTypes.bool.isRequired,
  layerVisible: PropTypes.bool.isRequired,
  exclude: PropTypes.array.isRequired,
  dataIdField: PropTypes.string.isRequired,
  tableOpen: PropTypes.bool.isRequired
}

function mapStateToProps (state, ownProps) {
  const titleid = state.table.titleid
  const tableOpen = state.table.open

  const layerConf = state.layers[titleid] || {}

  const layerAttributes = layerConf.attributes
  const layerPropsFilter = layerConf.propsFilter ?? null
  const title = layerConf.title ?? ''
  const layerParent = layerConf.parent ?? ''
  const layerType = layerConf.type ?? ''
  const layerSelected = layerConf.selected ?? false
  const layerVisible = layerConf.visible ?? false
  const exclude = layerConf.config?.spatial?.exclude ?? []

  const geojson = state.table.data

  const dataIdField = layerConf.config?.spatial?.id || '$id'

  // console.debug('LayerTable - mapStateToProps', titleid, dataIdField, layerConf, geojson)

  return {
    dataIdField,
    geojson,
    tableOpen,
    layerAttributes,
    layerConf,
    layerParent,
    layerPropsFilter,
    title,
    titleid,
    layerType,
    layerSelected,
    layerVisible,
    exclude
  }
}

const mapDispatchToProps = dispatch => {
  return {
    dispatchCloseTable: () => {
      dispatch(closeTable())
    },
    setLayerFilter: (titleid, propsFilter) => {
      dispatch(setLayerFilter(titleid, propsFilter))
    },
    setLayerPanelsFeatureOpen: (titleid, panelFeatureTitle) => {
      dispatch(setLayerPanelsFeatureOpen(titleid, panelFeatureTitle))
    },
    setTableStatus: (status) => {
      dispatch(setTableStatus(status))
    }
  }
}

const LayerTable = (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(LayerTable_)
  )
)

export default LayerTable
